// ---- Paths ----
// Footpaths along a polyline of waypoints.
//
// ONE archetype (deco verge / onRoad boost / minimap), TWO looks:
//   city — draped flagstone cobbles (separate mesh over the terrain)
//   dirt — NO bed mesh. Dirt is painted into the terrain vertex colors
//          (setTerrainRoadPaint + buildTerrainChunk in terrain.ts) so it IS
//          the grass surface, just brown. This module only scatters a few
//          decorative pebbles on dirt (visual, no colliders) + minimap dots.
//
// City cobbles are BAKED into chunk meshes (Phase C): each cobble is vertex-
// colored and merged, flushed every CHUNK_ROWS courses.

import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { makeToon } from "./toon.ts";
import { shade, rand } from "./color.ts";
import { heightAt } from "./terrain.ts";
import type { MapDot } from "./world.ts";
import type { RoadStyle } from "./content/schemas.ts";

export type { RoadStyle };

const cobbleGeo = new RoundedBoxGeometry(1, 1, 1, 2, 0.18);
cobbleGeo.deleteAttribute("uv");

const pebbleGeo = new THREE.SphereGeometry(1, 6, 5);
pebbleGeo.deleteAttribute("uv");
pebbleGeo.scale(1, 0.55, 1);

const CITY_STONE = 0x8f8779;
const CITY_EDGE = 0x6e5c41;
/** Minimap + pebble accent — match terrain dirt paint (terrain.ts DIRT_ROAD). */
const DIRT_DOT = 0xc9a87a;
const PEBBLE = 0xa89880;

const pathMat = makeToon({ vertexColors: true });

const CHUNK_ROWS = 48;
const STEP = 1.15;

type Waypoint = [number, number];

function paint(geo: THREE.BufferGeometry, color: number): THREE.BufferGeometry {
  const c = new THREE.Color(color);
  const n = geo.attributes.position.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(arr, 3));
  return geo;
}

/** Build a road. City → cobble mesh. Dirt → pebbles only (ground tint is terrain). */
function buildPath(
  waypoints: Waypoint[],
  width: number,
  dots: MapDot[],
  style: RoadStyle = "city",
): THREE.Group {
  if (style === "dirt") return buildDirtAccents(waypoints, width, dots);
  return buildCityPath(waypoints, width, dots);
}

function buildCityPath(
  waypoints: Waypoint[],
  width: number,
  dots: MapDot[],
): THREE.Group {
  const group = new THREE.Group();
  let parts: THREE.BufferGeometry[] = [];
  let rows = 0;
  const flush = () => {
    if (!parts.length) return;
    const merged = mergeGeometries(parts, false)!;
    parts.forEach((p) => p.dispose());
    parts = [];
    rows = 0;
    const mesh = new THREE.Mesh(merged, pathMat);
    mesh.receiveShadow = true;
    group.add(mesh);
  };

  let carry = 0;
  let dotCarry = 0;
  for (let s = 0; s < waypoints.length - 1; s++) {
    const [ax, az] = waypoints[s];
    const [bx, bz] = waypoints[s + 1];
    const dx = bx - ax, dz = bz - az;
    const segLen = Math.hypot(dx, dz);
    if (segLen < 1e-4) continue;
    const ux = dx / segLen, uz = dz / segLen;
    const angle = Math.atan2(ux, uz);
    const px = -uz, pz = ux;

    let t = carry;
    for (; t < segLen; t += STEP) {
      const cx = ax + ux * t, cz = az + uz * t;
      layCityRow(parts, cx, cz, px, pz, angle, width);
      if (++rows >= CHUNK_ROWS) flush();
      dotCarry += STEP;
      if (dotCarry >= 3) {
        dotCarry = 0;
        dots.push({ x: cx, z: cz, color: CITY_STONE, kind: "road" });
      }
    }
    carry = t - segLen;
  }
  flush();
  return group;
}

function layCityRow(
  parts: THREE.BufferGeometry[],
  cx: number, cz: number,
  px: number, pz: number,
  angle: number, width: number,
) {
  const half = width / 2;
  const cols = Math.max(2, Math.round(width / 0.95));
  const cw = width / cols;
  for (let i = 0; i < cols; i++) {
    const off = -half + (i + 0.5) * cw;
    const x = cx + px * off, z = cz + pz * off;
    const edge = Math.abs(off) > half - cw * 0.9;
    const base = edge ? CITY_EDGE : CITY_STONE;
    const col = shade(base, (rand(x * 7.3 + z * 13.1) - 0.5) * 0.16);
    const g = cobbleGeo.clone();
    g.scale(cw * 0.92, 0.18, cw * 0.92);
    g.rotateY(angle);
    g.translate(x, heightAt(x, z) + 0.04, z);
    parts.push(paint(g, col));
  }
}

/** Dirt accents only — ground brown lives on the terrain mesh. */
function buildDirtAccents(
  waypoints: Waypoint[],
  width: number,
  dots: MapDot[],
): THREE.Group {
  const group = new THREE.Group();
  let parts: THREE.BufferGeometry[] = [];
  let rows = 0;
  const flush = () => {
    if (!parts.length) return;
    const merged = mergeGeometries(parts, false)!;
    parts.forEach((p) => p.dispose());
    parts = [];
    rows = 0;
    const mesh = new THREE.Mesh(merged, pathMat);
    mesh.receiveShadow = true;
    mesh.castShadow = false;
    group.add(mesh);
  };

  let carry = 0;
  let dotCarry = 0;
  for (let s = 0; s < waypoints.length - 1; s++) {
    const [ax, az] = waypoints[s];
    const [bx, bz] = waypoints[s + 1];
    const dx = bx - ax, dz = bz - az;
    const segLen = Math.hypot(dx, dz);
    if (segLen < 1e-4) continue;
    const ux = dx / segLen, uz = dz / segLen;
    const px = -uz, pz = ux;

    let t = carry;
    for (; t < segLen; t += STEP) {
      const cx = ax + ux * t, cz = az + uz * t;
      scatterPebbles(parts, cx, cz, px, pz, width);
      if (++rows >= CHUNK_ROWS) flush();
      dotCarry += STEP;
      if (dotCarry >= 3) {
        dotCarry = 0;
        dots.push({ x: cx, z: cz, color: DIRT_DOT, kind: "road" });
      }
    }
    carry = t - segLen;
  }
  flush();
  return group;
}

/** Sparse pebbles on the painted dirt — visual only, no colliders. */
function scatterPebbles(
  parts: THREE.BufferGeometry[],
  cx: number, cz: number,
  px: number, pz: number,
  width: number,
) {
  // ~8% of samples, one small stone — quiet accents on the earth strip.
  if (rand(cx * 19.1 + cz * 31.7) > 0.08) return;
  const seed = cx * 41.3 + cz * 17.9;
  const half = width / 2 * 0.65;
  const across = (rand(seed) - 0.5) * 2 * half;
  const along = (rand(seed + 1) - 0.5) * STEP * 0.35;
  const tx = -pz, tz = px;
  const x = cx + px * across + tx * along;
  const z = cz + pz * across + tz * along;
  const size = 0.035 + rand(seed + 2) * 0.045;
  const col = shade(PEBBLE, (rand(seed + 3) - 0.5) * 0.12);
  const g = pebbleGeo.clone();
  g.scale(size, size * 0.6, size * (0.85 + rand(seed + 4) * 0.25));
  g.rotateY(rand(seed + 5) * Math.PI * 2);
  g.translate(x, heightAt(x, z) + size * 0.35, z);
  parts.push(paint(g, col));
}

export { buildPath };
export type { Waypoint };
